import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import { Search } from '@/components/common/Search';
import { Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { BlueCheckIcon, MinusIcon, MusicIcon, PlayIcon, PlusIcon } from '@/resources';
import { useSearchMusicQuery } from '@/services/music.service';
import { DownloadSong } from '@/types';

type SearchMusicResultsProps = {
  selectedSongs: DownloadSong[];
  setSelectedSongs: Dispatch<SetStateAction<DownloadSong[]>>;
  previewSongId?: number;
  setPreviewSongId: (id: number) => void;
  setPreviewSongName: (name: string) => void;
  hasMusicProviderAccess: boolean;
};

export const SearchMusicResults = ({
  selectedSongs,
  setSelectedSongs,
  previewSongId,
  setPreviewSongId,
  setPreviewSongName,
  hasMusicProviderAccess
}: SearchMusicResultsProps) => {
  const [t] = useTranslation('common');
  const [showMinLengthMessage, setShowMinLengthMessage] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const searchMusicQuery = useSearchMusicQuery(String(searchText), hasMusicProviderAccess);
  const music = hasMusicProviderAccess ? searchMusicQuery.data : null;
  const isLoading = hasMusicProviderAccess ? searchMusicQuery.isLoading : false;

  useEffect(() => {
    if (searchText.length > 0 && searchText.length < 2) {
      const timer = setTimeout(() => {
        setShowMinLengthMessage(true);
      }, 2000);

      return () => {
        clearTimeout(timer);
        setShowMinLengthMessage(false);
      };
    }
  }, [searchText]);

  const searchCallback = (query: string) => {
    setSearchText(String(query));
  };

  const handleAddSong = (song: DownloadSong) => {
    if (!selectedSongs.some((s) => s.id === song.id)) {
      setSelectedSongs((prev: DownloadSong[]) => [...prev, song]);
    }
  };

  const handleRemoveSong = (songId: number) => {
    setSelectedSongs((prev: DownloadSong[]) => prev.filter((s) => s.id !== songId));
  };

  return (
    <>
      <Search callback={searchCallback} disabled={!hasMusicProviderAccess} />
      <div className='mt-2 w-full'>
        {searchText.length < 2 ? (
          <div className='flex flex-col gap-2 py-2'>
            <Label
              text={t('searchForMusicOnline')}
              type={LABEL_TYPE.PARAGRAPH1}
              className='text-secondary-gray'
            />
            {showMinLengthMessage && (
              <Label
                text={t('nameMin')}
                type={LABEL_TYPE.PARAGRAPH1}
                className='!text-accent-red'
              />
            )}
          </div>
        ) : isLoading ? (
          <div className='flex items-center justify-center py-6'>
            <Spinner />
          </div>
        ) : music?.data.length === 0 ? (
          <Label
            text={t('noResults')}
            type={LABEL_TYPE.PARAGRAPH1}
            className='py-2 text-secondary-gray'
          />
        ) : (
          <div className='no-scrollbar flex max-h-40 flex-col overflow-y-auto rounded-md border border-secondary-blue-gray pr-4 scrollbar'>
            {music?.data.map((song: DownloadSong) => (
              <div key={song.id} className='flex flex-row items-center justify-between px-4 py-1'>
                <div className='flex w-[70%] flex-row items-center'>
                  <MusicIcon
                    fill={previewSongId === song.id ? 'var(--color-primary-blue)' : 'black'}
                    className='mr-4 mt-1 size-4 lg:size-6'
                  />
                  <p
                    className={clsx(
                      'w-[50vw] truncate',
                      previewSongId === song.id && 'font-semibold text-primary-blue'
                    )}>
                    {song.artist} - {song.title}
                  </p>
                </div>
                <div className='flex flex-row items-center gap-4'>
                  {song.duration && (
                    <p
                      className={clsx(
                        previewSongId === song.id
                          ? 'font-semibold text-primary-blue'
                          : 'text-secondary-gray'
                      )}>
                      {~~(song.duration / 60)}:
                      {song.duration % 60 < 10 ? `0${song.duration % 60}` : `${song.duration % 60}`}
                    </p>
                  )}
                  <PlayIcon
                    className={clsx(
                      'hover:text-primary-blue',
                      previewSongId === song.id ? 'text-primary-blue' : 'text-secondary-gray'
                    )}
                    onClick={() => {
                      setPreviewSongId(song.id);
                      setPreviewSongName(`${song.artist} - ${song.title}`);
                    }}
                  />
                  {selectedSongs.some((s) => s.id === song.id) ? (
                    <BlueCheckIcon className='text-primary-blue' />
                  ) : (
                    <PlusIcon
                      className='text-secondary-gray hover:text-primary-blue'
                      onClick={() => handleAddSong(song)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className='mt-4 flex max-h-[250px] min-h-[150px] w-full flex-col rounded-lg border border-secondary-blue-gray'>
        <Label text={t('selectedSongs')} className='px-4 py-2 !text-lg !text-primary-black' />
        {selectedSongs.length > 0 ? (
          <div className='no-scrollbar flex flex-col overflow-y-auto pr-4'>
            {selectedSongs.map((song) => (
              <div
                key={song.id}
                onClick={() => {
                  setPreviewSongId(song.id);
                  setPreviewSongName(`${song.artist} - ${song.title}`);
                }}
                className='flex cursor-pointer flex-row items-center justify-between px-4 py-1'>
                <div className='flex w-[70%] flex-row items-center'>
                  <MusicIcon
                    fill={previewSongId === song.id ? 'var(--color-primary-blue)' : 'black'}
                    className='mr-4 mt-1 size-4 lg:size-6'
                  />
                  <p
                    className={clsx(
                      'w-[50vw] truncate',
                      previewSongId === song.id && 'font-semibold text-primary-blue'
                    )}>
                    {song.artist} - {song.title}
                  </p>
                </div>
                <div className='flex flex-row items-center gap-4'>
                  {song.duration && (
                    <p
                      className={clsx(
                        previewSongId === song.id
                          ? 'font-semibold text-primary-blue'
                          : 'text-secondary-gray'
                      )}>
                      {~~(song.duration / 60)}:
                      {song.duration % 60 < 10 ? `0${song.duration % 60}` : `${song.duration % 60}`}
                    </p>
                  )}
                  <PlayIcon
                    className={clsx(
                      'hover:text-primary-blue',
                      previewSongId === song.id ? 'text-primary-blue' : 'text-secondary-gray'
                    )}
                    onClick={() => {
                      setPreviewSongId(song.id);
                      setPreviewSongName(`${song.artist} - ${song.title}`);
                    }}
                  />
                  <MinusIcon
                    className='text-xl font-bold text-secondary-gray hover:text-primary-blue'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveSong(song.id);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : hasMusicProviderAccess ? (
          <Label
            text={t('noSelectedSongs')}
            type={LABEL_TYPE.PARAGRAPH1}
            className='px-4 py-2 text-secondary-gray'
          />
        ) : (
          <Label
            text={t('yourOranizationDoesntHavePermission', {
              permission: t(`${ORGANIZATION_PERMISSION_TYPE.MUSIC_PROVIDER}`)
            })}
            type={LABEL_TYPE.PARAGRAPH1}
            className='px-4 py-2 text-secondary-gray'
          />
        )}
      </div>
    </>
  );
};
